import { create } from "zustand";
import type { DetailType } from "#shared/schemas/entityDetail.ts";

export interface TrailEntity {
  type: DetailType;
  id: string;
  label: string;
}

export interface SharedTracksDrawer {
  kind: "shared_tracks" | "graph_edge";
  artistId: string;
  artistLabel: string;
  withArtistId: string;
  withLabel: string;
}

/** Artist sheet section to focus when opening from the browse table. */
export type ArtistSheetFocus = "tracks" | "albums" | "playlists" | "collabs";

/** Non-artist sheet section focus (e.g. Ideas → samples). */
export type SheetSectionFocus = "samples";

interface BrowseTrailState {
  /** Path for the current dive only — reset when opening from the grid. */
  stack: TrailEntity[];
  /** Entities popped by Back — Forward restores them until a new push. */
  forward: TrailEntity[];
  drawer: SharedTracksDrawer | null;
  focusCollaborators: boolean;
  artistSheetFocus: ArtistSheetFocus | null;
  sheetSectionFocus: SheetSectionFocus | null;
  resetTo: (entity: TrailEntity) => void;
  push: (entity: TrailEntity) => void;
  jumpTo: (index: number) => void;
  back: () => void;
  goForward: () => void;
  openSharedTracks: (drawer: SharedTracksDrawer) => void;
  closeDrawer: () => void;
  setFocusCollaborators: (value: boolean) => void;
  setArtistSheetFocus: (value: ArtistSheetFocus | null) => void;
  setSheetSectionFocus: (value: SheetSectionFocus | null) => void;
  /** Fix crumb label once the entity detail title loads (e.g. URL hydrate). */
  relabelTop: (label: string) => void;
  clear: () => void;
}

/**
 * In-sheet dive trail. Fresh open from the table resets; only in-sheet hops push.
 * Hopping to an entity already in the trail truncates back to it (no loops).
 */
export const useBrowseTrailStore = create<BrowseTrailState>((set, get) => ({
  stack: [],
  forward: [],
  drawer: null,
  focusCollaborators: false,
  artistSheetFocus: null,
  sheetSectionFocus: null,

  resetTo: (entity) =>
    set({
      stack: [entity],
      forward: [],
      drawer: null,
      focusCollaborators: false,
      artistSheetFocus: null,
      sheetSectionFocus: null,
    }),

  push: (entity) =>
    set((state) => {
      const existing = state.stack.findIndex(
        (e) => e.type === entity.type && e.id === entity.id,
      );
      if (existing >= 0) {
        return {
          stack: [...state.stack.slice(0, existing), entity],
          forward: [],
          drawer: null,
          focusCollaborators: false,
        };
      }
      const top = state.stack[state.stack.length - 1];
      if (top && top.type === entity.type && top.id === entity.id) {
        return { drawer: null, focusCollaborators: false };
      }
      return {
        stack: [...state.stack, entity],
        forward: [],
        drawer: null,
        focusCollaborators: false,
      };
    }),

  jumpTo: (index) =>
    set((state) => {
      if (index < 0 || index >= state.stack.length) return state;
      return {
        stack: state.stack.slice(0, index + 1),
        forward: [],
        drawer: null,
        focusCollaborators: false,
      };
    }),

  back: () => {
    const { stack, drawer, forward } = get();
    if (drawer) {
      set({ drawer: null });
      return;
    }
    if (stack.length <= 1) {
      set({
        stack: [],
        forward: [],
        focusCollaborators: false,
        artistSheetFocus: null,
        sheetSectionFocus: null,
      });
      return;
    }
    const leaving = stack[stack.length - 1];
    if (!leaving) return;
    set({
      stack: stack.slice(0, -1),
      forward: [leaving, ...forward],
      focusCollaborators: false,
    });
  },

  goForward: () => {
    const { stack, forward } = get();
    const next = forward[0];
    if (!next) return;
    set({
      stack: [...stack, next],
      forward: forward.slice(1),
      drawer: null,
      focusCollaborators: false,
    });
  },

  openSharedTracks: (drawer) => set({ drawer }),

  closeDrawer: () => set({ drawer: null }),

  setFocusCollaborators: (focusCollaborators) =>
    set({
      focusCollaborators,
      artistSheetFocus: focusCollaborators ? "collabs" : null,
    }),

  setArtistSheetFocus: (artistSheetFocus) =>
    set({
      artistSheetFocus,
      focusCollaborators: artistSheetFocus === "collabs",
    }),

  setSheetSectionFocus: (sheetSectionFocus) => set({ sheetSectionFocus }),

  relabelTop: (label) =>
    set((state) => {
      const top = state.stack[state.stack.length - 1];
      if (!top || top.label === label) return state;
      return {
        stack: [...state.stack.slice(0, -1), { ...top, label }],
      };
    }),

  clear: () =>
    set({
      stack: [],
      forward: [],
      drawer: null,
      focusCollaborators: false,
      artistSheetFocus: null,
      sheetSectionFocus: null,
    }),
}));
